// Types
import { OrderType } from '../types/orders';

// Constants
import {
  directoryNaming,
  formAccidentNaming,
} from '../components/forms/AddBasicInfoAccidentForm/constants';
import { formVehicleNaming } from '../components/forms/AddVehicleForm/constants';
import {
  formActionNaming,
  // 🔴 El de ACTUACIÓN (`canvas-action`). Hasta el 2026-11-02 esta limpieza
  // usaba el `directoryNaming` de ACCIDENTE (el de arriba): empezar o
  // terminar una actuación vaciaba `canvas-accident` —el croquis de un alta o
  // una edición de accidente a medias— y nunca `canvas-action` (revisión de
  // la Task 9, spec wizard-draft-ownership).
  directoryNaming as actionDirectoryNaming,
} from '../components/forms/ActionForms/constants';
import { formNaming as formSurveillanceRouteNaming } from '../components/forms/SurveillanceRouteForm/constants';

// Utils
import { deleteWholeDirectory } from './files';
import { clearExcludedVehicles } from './excludedVehiclesSync';

// Redux
import { actionTabsReset } from '../state/actions/ui/actionTabs';
import { store } from '../state/configureStore';

/**
 * Limpia el formulario de accidente. Por defecto, el de la pantalla; el
 * reintento de la cola pasa su contexto en memoria para no borrar el formulario
 * abierto (spec outbox-autocontenido v2 § 5.1). Espera a que las carpetas se
 * vacíen: antes se lanzaba el borrado y se seguía (inventario, hallazgo 19).
 */
export const removeNewAccidentStorage = async (
  ctx: {
    store: Pick<Storage, 'removeItem'>;
    images: { clear(directory: string): Promise<void> };
  } = { store: sessionStorage, images: { clear: deleteWholeDirectory } },
): Promise<void> => {
  Object.values({ ...formAccidentNaming, ...formVehicleNaming }).forEach(
    (key) => {
      ctx.store.removeItem(key);
    }
  );
  await Promise.all([
    ctx.images.clear(directoryNaming.CANVAS),
    ctx.images.clear(OrderType.Accident),
  ]);
};

export const removeNewActionStorage = () => {
  // Limpia TODOS los valores de formActionNaming (incluye partidas, sketch, etc.)
  Object.values(formActionNaming).forEach((key) => {
    sessionStorage.removeItem(key);
  });

  // ========== LIMPIAR FORMULARIOS TEMPORALES CON SUFIJOS ==========
  // Formularios de operadores (tabs interno/externo + caché de conductores
  // por-wizard — BUG borrador RV 2026-07-12)
  sessionStorage.removeItem(`${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_interno`);
  sessionStorage.removeItem(`${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_externals`);
  sessionStorage.removeItem(`${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_drivers`);
  
  // Formularios de equipamiento (añadir vehículo/maquinaria)
  sessionStorage.removeItem(
    `${formActionNaming.NEW_WORK_EQUIPMENT_FORM}-vehicle`
  );
  sessionStorage.removeItem(
    `${formActionNaming.NEW_WORK_EQUIPMENT_FORM}-machinery`
  );

  // Limpia directorios de archivos temporales
  deleteWholeDirectory(actionDirectoryNaming.CANVAS); // Sketch/croquis (`canvas-action`)
  deleteWholeDirectory(OrderType.Action); // Imágenes normales

  // Vehículos de obra que el usuario quitó en Maquinaria: son de ESTA
  // actuación. La lista ya va atada a su id (`excludedVehiclesSync`), pero una
  // actuación nueva que todavía no se ha creado en el servidor no lo tiene, y
  // no debe heredar la de otra en la misma situación.
  clearExcludedVehicles(false);

  // Reset de tabs
  store.dispatch(actionTabsReset() as any);
};

// ═══════════════════════════════════════════════════════════════════
// SURVEILLANCE ROUTE - Limpieza de sessionStorage
// ═══════════════════════════════════════════════════════════════════

/** Buckets con sufijo que cuelgan de la key base de un borrador de Operarios
 *  (BUG borrador RV 2026-07-12). La key base la borra quien llame. */
const OPERATORS_DRAFT_SUFFIXES = [
  '_drivers',
  '_interno',
  '_externals',
  // `_opid`: clientOpId estable mientras dure el asistente (evita que un
  // reintento tras un timeout cree DOS recorridos). Se retira con el borrador
  // porque cada asistente es una operación lógica distinta.
  '_opid',
];

const removeOperatorsDraft = (baseKey: string) => {
  sessionStorage.removeItem(baseKey);
  OPERATORS_DRAFT_SUFFIXES.forEach((suffix) =>
    sessionStorage.removeItem(`${baseKey}${suffix}`)
  );
};

/**
 * Purga el borrador del asistente de Recorregut de Vigilància.
 *
 * `scope`:
 * - `'all'` (por defecto) — todo: creación + edición. Es lo que quiere el
 *   logout y lo que hacía esta función desde siempre.
 * - `'new'` / `'edit'` — solo el borrador de ese wizard, para que entrar a
 *   crear no toque lo que hay a medias en edición y viceversa.
 *
 * 🔴 **La tripulación del recorrido se deriva de ESTE borrador, no de lo que
 * la pantalla enseña** (`deriveCrew` sobre la key, en `NewRoute/saveData.ts`).
 * Por eso un borrador que sobreviva a un intento abandonado no es un resto
 * inocuo: se convierte en operarios y vehículos REALES del siguiente
 * recorrido. Incidente 2026-08-08 (app Android, donde el WebView no se cierra
 * nunca y la sesión dura días). Spec:
 * `docs/superpowers/specs/2026-08-08-rv-tripulacion-fantasma-design.md`.
 */
export const removeNewSurveillanceRouteStorage = (
  scope: 'all' | 'new' | 'edit' = 'all'
) => {
  const {
    NEW_SURVEILLANCE_ROUTE_OPERATORS_FORM: newOperatorsKey,
    EDIT_SURVEILLANCE_ROUTE_OPERATORS_FORM: editOperatorsKey,
    ...otherKeys
  } = formSurveillanceRouteNaming;

  if (scope !== 'edit') removeOperatorsDraft(newOperatorsKey);
  if (scope !== 'new') removeOperatorsDraft(editOperatorsKey);

  // Resto de keys del módulo (formularios base del wizard): solo en la purga
  // completa — no son de ningún wizard en concreto.
  if (scope === 'all') {
    Object.values(otherKeys).forEach((key) => sessionStorage.removeItem(key));
  }
};
